import { getTransactionTypeData } from '../../../../containers/CheckoutPage/CheckoutPageTransactionHelpers';
import { getProcess } from '../../../../transactions/transaction';

/**
 * EXT-DEFERRED-PAYOUT(client-detection) — helpers shared by the ListingPage
 * variants (entry point) and the DeferredCheckoutPage (consumer).
 */

/**
 * Resolve the deferred request transition from the listing's process. Returns null for
 * listings whose process has no deferred branch (e.g. inquiry/negotiation, or a custom
 * process the operator didn't merge the branch into).
 *
 * @param {Object} listing denormalised listing entity
 * @returns {null | string} the transition/request-payment-deferred name
 */
export const getDeferredRequestTransition = listing => {
  const processName = listing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
  try {
    return processName ? getProcess(processName)?.transitions?.REQUEST_PAYMENT_DEFERRED : null;
  } catch (e) {
    return null;
  }
};

/**
 * A listing goes through the deferred-payout checkout when the marketplace-level switch
 * (REACT_APP_DEFERRED_PAYOUT_ENABLED) is on AND the listing's transaction process has
 * the deferred branch. A provider needs NO pre-provisioning: empavision mints their
 * Stripe connected account lazily at first checkout (create-intent) and writes it back
 * to profile.publicData.deferredPayoutAccountId, so `accountId` here may be null and is
 * only ever used as a tamper cross-check; notifyEmail/firstName remain for the legacy
 * flat-price (standalone) path. The provider's real identity is always resolved
 * SERVER-SIDE by empavision from the transaction's provider record.
 *
 * @param {Object} author denormalised author entity (from listings.show include=author)
 * @param {Object} listing denormalised listing entity (for the process check)
 * @returns {null | { accountId, notifyEmail, firstName }}
 */
export const getDeferredPayoutProvider = (author, listing) => {
  const enabled = process.env.REACT_APP_DEFERRED_PAYOUT_ENABLED === 'true';
  if (!enabled || !getDeferredRequestTransition(listing)) {
    return null;
  }
  const profile = author?.attributes?.profile;
  const pub = profile?.publicData || {};
  const displayName = profile?.displayName || '';
  return {
    // Optional: set once empavision has minted the provider's account (or by hand).
    accountId: pub.deferredPayoutAccountId || null,
    notifyEmail: pub.deferredPayoutNotifyEmail || null,
    // firstName is protected (not exposed cross-user); derive a friendly name from displayName.
    firstName: displayName.split(' ')[0] || null,
  };
};

/**
 * EXT-DEFERRED-PAYOUT — build the params for the privileged
 * transition/request-payment-deferred initiate from the OrderPanel-produced orderData.
 * Mirrors CheckoutPage's getOrderParams + initiateOrder packing (quantity →
 * stockReservationQuantity, bookingDates spread to bookingStart/bookingEnd, transaction
 * type data + delivery method into protectedData) so the server's line-item computation
 * (server/api/initiate-privileged.js) sees the exact same shape as the standard flow.
 *
 * @param {Object} orderData from ListingPage's handleDeferredSubmit
 * @param {Object} listing denormalised listing entity
 * @param {Object} config app config (for getTransactionTypeData)
 * @returns {{ params: Object, serverOrderData: Object }} Marketplace API transition
 *   params, plus the client-server-only orderData hint (deliveryMethod for shipping fees).
 */
export const getDeferredInitiateParams = (orderData, listing, config) => {
  const { listingType, unitType, priceVariants } = listing?.attributes?.publicData || {};
  const { bookingDates, quantity, seats, deliveryMethod, priceVariantName } = orderData || {};

  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const seatsMaybe = seats ? { seats } : {};
  const bookingMaybe = bookingDates
    ? { bookingStart: bookingDates.bookingStart, bookingEnd: bookingDates.bookingEnd }
    : {};
  const priceVariantNameMaybe = priceVariantName ? { priceVariantName } : {};
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  // Prefix chosen price-variant properties into protectedData (bookkeeping parity with
  // CheckoutPage's prefixPriceVariantProperties).
  const priceVariant = priceVariants?.find(pv => pv.name === priceVariantName);
  const priceVariantProtectedMaybe = priceVariant
    ? Object.fromEntries(
        Object.entries(priceVariant).map(([key, value]) => [
          `priceVariant${key.charAt(0).toUpperCase()}${key.substr(1)}`,
          value,
        ])
      )
    : {};

  const protectedData = {
    ...getTransactionTypeData(listingType, unitType, config),
    ...deliveryMethodMaybe,
    ...priceVariantProtectedMaybe,
  };

  return {
    params: {
      listingId: listing.id,
      ...bookingMaybe,
      ...quantityMaybe,
      ...seatsMaybe,
      ...priceVariantNameMaybe,
      protectedData,
    },
    serverOrderData: deliveryMethodMaybe,
  };
};
